// Spec ref: docs/superpowers/specs/2026-05-21-backend-extension-ui-wireup.md (Phase 4)
// Sopralluogo detail — workflow status timeline + cliente/lotto cross-refs +
// custode card + activity log. Reuses Appointment + ActivityTimeline.
import { useState } from 'react'
import {
  createFileRoute,
  Link,
  redirect,
  useRouter,
} from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { hasTokens } from '../lib/auth'
import {
  useAppointment,
  usePatchAppointment,
} from '../hooks/useAppointments'
import { useMe } from '../hooks/useAuth'
import { canWriteParticipation } from '../lib/permissions'
import { ActivityTimeline } from '../components/ActivityTimeline'
import { CommentsButton } from '../components/CommentsButton'
import { NewChatModal } from '../components/NewChatModal'
import { CardSkeleton } from '../components/Skeleton'
import { ProfessionalPicker } from '../components/ProfessionalPicker'
import {
  ApiError,
  fetchCalendarBotStatus,
  fetchCalendarStatus,
  syncAppointmentToCalendar,
  unsyncAppointmentFromCalendar,
  richiediScdFattibilita,
  duplicaRichiesta,
  registraSollecito,
  patchAppointment,
  patchAppointmentCustode,
  type SollecitoBody,
  type AppointmentRow,
  type AppointmentStatus,
  type CalendarBotStatusResponse,
  type CalendarStatusResponse,
  type CalendarSyncResponse,
} from '../lib/api'
import {
  STATUS_ORDER,
  STATUS_LABEL,
  STATUS_VARIANT,
} from '../lib/sopralluogoStatus'
import {
  CLOSE_OPTIONS,
  outcomeReasonLabel,
  type OutcomeReason,
} from '../lib/outcomeReason'

export const Route = createFileRoute('/sopralluoghi/$id')({
  beforeLoad: () => {
    if (!hasTokens()) {
      throw redirect({ to: '/login' })
    }
  },
  component: SopralluogoDetailPage,
})

// Workflow (happy path): richiesta_da_fare -> richiesta_fatta ->
// attesa_riscontro_cliente ("Attesa conferma Cliente") -> fissato
// ("Sopralluogo Confermato") -> effettuato. STATUS_ORDER/LABEL/VARIANT sono
// nel modulo condiviso ../lib/sopralluogoStatus (single source of truth con la
// lista). Il modal data/comunica parte su ->attesa_riscontro_cliente; la sync
// Google Calendar parte GIA' da quello stato (evento "In attesa di conferma")
// e si aggiorna su ->fissato. Esiti laterali (cancelled/noshow/
// non_effettuato/sospeso) fuori dal path, selezionabili da buttons separati.

const KIND_LABEL: Record<string, string> = {
  sopralluogo: 'Sopralluogo',
  call: 'Chiamata',
  scheda: 'Scheda',
  asta: 'Asta',
  app_presenza: 'Appuntamento in presenza',
  app_online: 'Appuntamento online',
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function shortCodeC(n: number | null): string {
  if (n === null || n === undefined) return ''
  return `C_${n.toString().padStart(5, '0')}`
}
function shortCodeL(n: number | null): string {
  if (n === null || n === undefined) return ''
  return `L_${n.toString().padStart(5, '0')}`
}
function shortCodeS(n: number | null): string {
  if (n === null || n === undefined) return ''
  return `S_${n.toString().padStart(5, '0')}`
}

function StatusTimeline({
  current,
  onChange,
  disabled,
}: {
  current: AppointmentStatus
  onChange: (next: AppointmentStatus) => void
  disabled: boolean
}) {
  const idx = STATUS_ORDER.indexOf(current)
  return (
    <ol
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${STATUS_ORDER.length}, 1fr)`,
        gap: 0,
        listStyle: 'none',
        padding: 0,
        margin: 0,
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-md)',
        overflow: 'hidden',
      }}
    >
      {STATUS_ORDER.map((s, i) => {
        const reached = idx >= 0 && i <= idx
        const isCurrent = s === current
        return (
          <li key={s} style={{ minWidth: 0 }}>
            <button
              type="button"
              onClick={() => onChange(s)}
              disabled={disabled}
              aria-current={isCurrent ? 'step' : undefined}
              style={{
                width: '100%',
                padding: 'var(--sp-2) var(--sp-3)',
                background: isCurrent
                  ? 'var(--accent-soft)'
                  : reached
                    ? 'var(--ok-soft)'
                    : 'var(--bg-2)',
                color: isCurrent
                  ? 'var(--accent-ink)'
                  : reached
                    ? 'var(--ok-ink)'
                    : 'var(--ink-3)',
                border: 0,
                borderRight:
                  i < STATUS_ORDER.length - 1
                    ? '1px solid var(--line)'
                    : 'none',
                fontSize: 'var(--fs-12)',
                fontWeight: isCurrent
                  ? 'var(--fw-semibold)'
                  : 'var(--fw-medium)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: reached
                    ? isCurrent
                      ? 'var(--accent-ink)'
                      : 'var(--ok-ink)'
                    : 'var(--bg-3)',
                  color: '#fff',
                  fontSize: 10,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                {reached ? '✓' : i + 1}
              </span>
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {STATUS_LABEL[s]}
              </span>
            </button>
          </li>
        )
      })}
    </ol>
  )
}

// Badge scadenza conferma cliente: countdown "N giorni"; se scaduta senza
// conferma → da chiudere come "Mancata conferma" (Q2 Michele).
// Stati terminali: pratica chiusa → mostra "Riapri" invece di "Chiudi".
const TERMINAL_STATUSES = [
  'effettuato',
  'non_effettuato',
  'cancelled',
  'noshow',
  'sospeso',
]

function ScadenzaBadge({ iso, confirmed }: { iso: string; confirmed: boolean }) {
  const d = new Date(iso)
  const dateStr = d.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  if (confirmed) {
    return <span className="chip ok">{dateStr} · confermato</span>
  }
  // Diff in GIORNI-CALENDARIO (mezzanotte locale), non in ms: la scadenza è
  // salvata a fine giornata (23:59) e Math.ceil sui ms la spingeva a "domani"
  // anche quando scade oggi. round assorbe i giorni DST da 23h/25h.
  const startOfDay = (t: Date) =>
    new Date(t.getFullYear(), t.getMonth(), t.getDate())
  const days = Math.round(
    (startOfDay(d).getTime() - startOfDay(new Date()).getTime()) / 86_400_000,
  )
  if (days < 0) {
    return (
      <span className="chip risk" title="Cliente non ha confermato in tempo">
        {dateStr} · scaduta — chiudi come "Mancata conferma"
      </span>
    )
  }
  return (
    <span className={`chip ${days <= 1 ? 'warn' : 'info'}`}>
      {dateStr} · {days === 0 ? 'scade oggi' : `tra ${days}g`}
    </span>
  )
}

function Kv({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="kv">
      <span className="k">{k}</span>
      <span className="v">{children}</span>
    </div>
  )
}

function SopralluogoDetailPage() {
  const { id } = Route.useParams()
  const router = useRouter()
  const qc = useQueryClient()
  const { data: me } = useMe()
  const { data, isLoading, isError } = useAppointment(id)
  const patch = usePatchAppointment(id)
  const [editingNotes, setEditingNotes] = useState(false)
  const [notesDraft, setNotesDraft] = useState('')
  const [communicateOpen, setCommunicateOpen] = useState(false)
  const [custodeOpen, setCustodeOpen] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)
  const canEdit = canWriteParticipation(me)

  const calendarStatusQ = useQuery<CalendarStatusResponse, ApiError>({
    queryKey: ['calendar', 'status'],
    queryFn: fetchCalendarStatus,
    staleTime: 60_000,
    retry: false,
  })

  const calendarBotQ = useQuery<CalendarBotStatusResponse, ApiError>({
    queryKey: ['calendar', 'bot-status'],
    queryFn: fetchCalendarBotStatus,
    staleTime: 5 * 60_000,
    retry: false,
  })

  const syncMut = useMutation<CalendarSyncResponse, ApiError, void>({
    mutationFn: () => syncAppointmentToCalendar(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['appointment', id] })
      void qc.invalidateQueries({ queryKey: ['appointments'] })
    },
  })

  const unsyncMut = useMutation<void, ApiError, void>({
    mutationFn: () => unsyncAppointmentFromCalendar(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['appointment', id] })
      void qc.invalidateQueries({ queryKey: ['appointments'] })
    },
  })

  // Fase 2 — azioni post-visita (Effettuato).
  const scdMut = useMutation<AppointmentRow, ApiError, void>({
    mutationFn: () => richiediScdFattibilita(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['appointment', id] })
      void qc.invalidateQueries({ queryKey: ['appointments'] })
    },
  })
  const duplicaMut = useMutation<AppointmentRow, ApiError, void>({
    mutationFn: () => duplicaRichiesta(id),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ['appointments'] })
      void router.navigate({
        to: '/sopralluoghi/$id',
        params: { id: created.id },
        search: {} as never,
      })
    },
  })

  // Fase 2c — solleciti (custode/cliente) con nota auto.
  const [sollCustodeOpen, setSollCustodeOpen] = useState(false)
  const sollecitoMut = useMutation<AppointmentRow, ApiError, SollecitoBody>({
    mutationFn: (body) => registraSollecito(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['appointment', id] })
      void qc.invalidateQueries({ queryKey: ['appointments'] })
      setSollCustodeOpen(false)
    },
  })

  // Fase 2c — Riprogramma (cliente impedito): chiudi cancelled+rifare, poi
  // duplica con snooze riattiva_il (la nuova riappare in "Richiesta da fare"
  // solo da quella data).
  const [riprogrammaOpen, setRiprogrammaOpen] = useState(false)
  const [chiudiOpen, setChiudiOpen] = useState(false)
  const [editInteresse, setEditInteresse] = useState(false)
  const [interesseModalOpen, setInteresseModalOpen] = useState(false)
  const riprogrammaMut = useMutation<AppointmentRow, ApiError, string>({
    mutationFn: async (riattivaIso) => {
      await patchAppointment(id, {
        status: 'cancelled',
        outcome_reason: 'rifare',
      })
      return duplicaRichiesta(id, riattivaIso)
    },
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ['appointments'] })
      setRiprogrammaOpen(false)
      void router.navigate({
        to: '/sopralluoghi/$id',
        params: { id: created.id },
        search: {} as never,
      })
    },
  })

  function onStatusChange(next: AppointmentStatus) {
    if (!canEdit) return
    // Hop 'attesa_riscontro_cliente' ("Attesa conferma Cliente") -> apri modal:
    // raccoglie data/ora (scheduled_at) + invia email/WhatsApp al cliente per la
    // conferma. Il backend mette gia' l'evento su Google Calendar con
    // "In attesa di conferma" e lo aggiorna su ->fissato ("Sopralluogo
    // Confermato").
    if (next === 'attesa_riscontro_cliente') {
      setCommunicateOpen(true)
      return
    }
    patch.mutate({ status: next })
  }

  // Riapri una pratica chiusa (cliente ci ripensa) — feedback #1. Torna a
  // "Richiesta da fare" e azzera il motivo di chiusura.
  function reopen() {
    if (!canEdit) return
    patch.mutate({ status: 'richiesta_da_fare', outcome_reason: null })
  }

  function startEditNotes(s: AppointmentRow) {
    setNotesDraft(s.notes ?? '')
    setEditingNotes(true)
  }
  function saveNotes() {
    patch.mutate(
      { notes: notesDraft },
      { onSuccess: () => setEditingNotes(false) },
    )
  }

  const sopCode = data ? shortCodeS(data.short_code) : ''

  return (
    <>
      {data && (
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--accent-border)',
            borderRadius: 'var(--r-md)',
            padding: 'var(--sp-2) var(--sp-3)',
            margin: '0 0 var(--sp-3) 0',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-3)',
            flexWrap: 'wrap',
            fontSize: 'var(--fs-12)',
          }}
        >
          <button
            type="button"
            className="btn sm"
            onClick={() => {
              // Preserva tab/filtro precedente: usa history.back se possibile
              // (history len > 1) altrimenti fallback su lista default.
              if (window.history.length > 1) {
                router.history.back()
              } else {
                void router.navigate({
                  to: '/sopralluoghi',
                  search: {
                    page: 1,
                    page_size: 50,
                    q: '',
                    sort: '-scheduled_at',
                  },
                })
              }
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '4px 10px',
            }}
            title="Torna indietro"
          >
            ← Indietro
          </button>
          <span className="tag code">{sopCode}</span>
          <span style={{ fontWeight: 'var(--fw-semibold)' }}>
            {KIND_LABEL[data.kind] ?? data.kind}
          </span>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="btn sm"
            onClick={() => setChatOpen(true)}
            title="Apri chat di team su questo sopralluogo"
          >
            🗨️ Chat team
          </button>
          <CommentsButton
            entityType="appointment"
            entityId={id}
            title={sopCode}
          />
        </div>
      )}

      {isLoading && <CardSkeleton />}
      {isError && (
        <div
          className="muted"
          style={{ padding: 'var(--sp-4)', fontStyle: 'italic' }}
        >
          Sopralluogo non trovato o errore di caricamento.
        </div>
      )}

      {data && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3)',
          }}
        >
          {/* STATUS TIMELINE */}
          <section className="section" id="stato">
            <div className="section-head">
              <div className="title">Workflow</div>
              <div
                style={{
                  marginLeft: 'auto',
                  display: 'flex',
                  gap: 'var(--sp-1)',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                }}
              >
                <span
                  className={`chip ${STATUS_VARIANT[data.status] ?? 'outline'}`}
                >
                  {STATUS_LABEL[data.status] ?? data.status}
                </span>
                {outcomeReasonLabel(data.outcome_reason) && (
                  <span className="chip outline" title="Motivo chiusura">
                    {outcomeReasonLabel(data.outcome_reason)}
                  </span>
                )}
                {data.esito_interesse === true && (
                  <span className="chip ok" title="Cliente interessato">✅ Interessato</span>
                )}
                {data.esito_interesse === false && (
                  <span className="chip risk" title={data.motivo_non_interesse ?? 'Non interessa'}>
                    ❌ Non interessa{data.motivo_non_interesse ? ` — ${data.motivo_non_interesse}` : ''}
                  </span>
                )}
                {data.status === 'effettuato' && canEdit && (
                  <button
                    type="button"
                    className="btn xs ghost"
                    onClick={() => setEditInteresse((v) => !v)}
                    title="Modifica interesse cliente"
                    style={{ padding: '2px 6px', fontSize: 'var(--fs-11)' }}
                  >
                    {data.esito_interesse === null ? '+ Interesse' : '✏️'}
                  </button>
                )}
              </div>
              {editInteresse && data.status === 'effettuato' && (
                <InteresseInlineEditor
                  current={data.esito_interesse}
                  currentMotivo={data.motivo_non_interesse ?? ''}
                  pending={patch.isPending}
                  onSave={(interesse, motivo) => {
                    patch.mutate(
                      { esito_interesse: interesse, ...(motivo ? { motivo_non_interesse: motivo } : { motivo_non_interesse: null }) },
                      { onSuccess: () => setEditInteresse(false) },
                    )
                  }}
                  onCancel={() => setEditInteresse(false)}
                />
              )}
            </div>
            <div
              className="section-body"
              style={{ padding: 'var(--sp-3) var(--sp-4)' }}
            >
              <StatusTimeline
                current={data.status as AppointmentStatus}
                onChange={onStatusChange}
                disabled={!canEdit || patch.isPending}
              />
              {/* Azioni Marika — solleciti con nota auto (Fase 2c) */}
              {canEdit && data.status === 'richiesta_fatta' && (
                <div style={{ marginTop: 'var(--sp-2)' }}>
                  <button
                    type="button"
                    className="btn sm"
                    onClick={() => setSollCustodeOpen(true)}
                    disabled={sollecitoMut.isPending}
                  >
                    📞 Registra sollecito custode
                  </button>
                </div>
              )}
              {canEdit && data.status === 'attesa_riscontro_cliente' && (
                <div
                  style={{
                    marginTop: 'var(--sp-2)',
                    display: 'flex',
                    gap: 'var(--sp-2)',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <button
                    type="button"
                    className="btn sm"
                    onClick={() => sollecitoMut.mutate({ tipo: 'cliente' })}
                    disabled={sollecitoMut.isPending}
                  >
                    📞 Sollecito cliente
                  </button>
                  {data.primo_sollecito_cliente_at && (
                    <span
                      className="chip ok"
                      style={{ fontSize: 'var(--fs-10)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      title="Cliente già sollecitato"
                    >
                      ✓ sollecitato il{' '}
                      {new Date(data.primo_sollecito_cliente_at).toLocaleDateString('it-IT')}
                      <button
                        type="button"
                        title="Resetta flag sollecito"
                        onClick={() => patch.mutate({ primo_sollecito_cliente_at: null })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: 'inherit', opacity: 0.6, fontSize: 'var(--fs-10)' }}
                      >✕</button>
                    </span>
                  )}
                </div>
              )}
              {/* Stati laterali fuori dal happy-path — sempre disponibili */}
              {canEdit && (
                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--sp-2)',
                    flexWrap: 'wrap',
                    marginTop: 'var(--sp-2)',
                    paddingTop: 'var(--sp-2)',
                    borderTop: '1px solid var(--line)',
                    alignItems: 'center',
                  }}
                >
                  <span
                    className="muted"
                    style={{
                      fontSize: 'var(--fs-10)',
                      textTransform: 'uppercase',
                      letterSpacing: 'var(--tracking-caps)',
                      fontWeight: 'var(--fw-semibold)',
                      marginRight: 'var(--sp-2)',
                    }}
                  >
                    {TERMINAL_STATUSES.includes(data.status)
                      ? 'Pratica chiusa'
                      : 'Azioni'}
                  </span>
                  {TERMINAL_STATUSES.includes(data.status) ? (
                    <SideStatusBtn
                      icon="↩"
                      label="Riapri"
                      onClick={reopen}
                      disabled={patch.isPending}
                    />
                  ) : (
                    <>
                      <SideStatusBtn
                        icon="🔒"
                        label="Chiudi sopralluogo"
                        onClick={() => setChiudiOpen(true)}
                        disabled={patch.isPending}
                      />
                      <SideStatusBtn
                        icon="🔁"
                        label="Riprogramma"
                        onClick={() => setRiprogrammaOpen(true)}
                        disabled={patch.isPending || riprogrammaMut.isPending}
                      />
                    </>
                  )}
                </div>
              )}
              {patch.isError && (
                <div
                  className="chip risk"
                  style={{ marginTop: 'var(--sp-2)' }}
                  role="alert"
                >
                  Errore: {patch.error?.message}
                </div>
              )}

              {/* SCD fattibilità: disponibile in qualsiasi stato (anche chiusura negativa) */}
              {canEdit && (
                <div
                  style={{
                    marginTop: 'var(--sp-2)',
                    paddingTop: 'var(--sp-2)',
                    borderTop: '1px solid var(--line)',
                    display: 'flex',
                    gap: 'var(--sp-2)',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  {data.scheda_fattibilita_richiesta ? (
                    <>
                      <span className="chip ok" style={{ fontSize: 'var(--fs-11)' }}>
                        📋 SCD fattibilità richiesta
                      </span>
                      <Link
                        to="/scd-board"
                        className="btn sm ghost"
                        style={{ fontSize: 'var(--fs-11)' }}
                      >
                        Vedi sul board →
                      </Link>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn sm"
                      onClick={() => scdMut.mutate()}
                      disabled={scdMut.isPending}
                    >
                      📋 Richiedi SCD fattibilità
                    </button>
                  )}
                  {data.status === 'effettuato' && data.modalita === 'remoto' && (
                    <button
                      type="button"
                      className="btn sm"
                      onClick={() => duplicaMut.mutate()}
                      disabled={duplicaMut.isPending}
                      title="Crea una nuova richiesta per il sopralluogo fisico"
                    >
                      📍 Richiesta sopralluogo fisico
                    </button>
                  )}
                  {(scdMut.isError || duplicaMut.isError) && (
                    <span className="chip risk" style={{ fontSize: 'var(--fs-11)' }}>
                      Errore: {(scdMut.error ?? duplicaMut.error)?.message}
                    </span>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* CROSS REFS */}
          <section className="section" id="riferimenti">
            <div className="section-head">
              <div className="title">Cliente &amp; Lotto</div>
            </div>
            <div className="section-body">
              <div className="kv-grid">
                {data.client_id && (
                  <Kv k="Cliente">
                    <Link
                      to="/clienti/$id"
                      params={{ id: data.client_id }}
                      search={{} as never}
                      className="link"
                    >
                      <span className="tag code">
                        {shortCodeC(data.client_short_code)}
                      </span>{' '}
                      {data.client_name}
                    </Link>
                    {data.client_phone && (
                      <span className="sec muted mono">
                        {' '}
                        · {data.client_phone}
                      </span>
                    )}
                  </Kv>
                )}
                {data.property_id && (
                  <Kv k="Lotto">
                    <Link
                      to="/lotti/$id"
                      params={{ id: data.property_id }}
                      search={{} as never}
                      className="link"
                    >
                      <span className="tag code">
                        {shortCodeL(data.property_short_code)}
                      </span>{' '}
                      {data.property_address}
                      {data.property_comune && ` · ${data.property_comune}`}
                    </Link>
                    {data.property_rge && (
                      <span className="sec muted mono">
                        {' '}
                        · RGE {data.property_rge}
                      </span>
                    )}
                  </Kv>
                )}
                {data.property_data_asta && (
                  <Kv k="Data asta lotto">
                    {fmtDateTime(data.property_data_asta)}
                  </Kv>
                )}
              </div>
            </div>
          </section>

          {/* SCHEDULING + LOCATION */}
          <section className="section" id="scheduling">
            <div className="section-head">
              <div className="title">Programmazione</div>
            </div>
            <div className="section-body">
              <div className="kv-grid">
                <Kv k="Richiesta">{fmtDateTime(data.data_richiesta)}</Kv>
                <Kv k="Schedulato">{fmtDateTime(data.scheduled_at)}</Kv>
                {data.modalita && (
                  <Kv k="Modalità">
                    <span
                      className={`chip ${data.modalita === 'remoto' ? 'info' : 'outline'}`}
                    >
                      {data.modalita === 'remoto' ? '💻 Remoto' : '📍 Fisico'}
                    </span>
                  </Kv>
                )}
                {data.scadenza_conferma_cliente && (
                  <Kv k="Scadenza conferma cliente">
                    <ScadenzaBadge
                      iso={data.scadenza_conferma_cliente}
                      confirmed={!!data.cliente_confermato_at}
                    />
                  </Kv>
                )}
                <Kv k="Sede">{data.location ?? '—'}</Kv>
                <Kv k="Owner">{data.owner_name ?? '—'}</Kv>
                {data.coordinator_name && (
                  <Kv k="Coordinatore">{data.coordinator_name}</Kv>
                )}
                {data.cliente_confermato_at && (
                  <Kv k="Cliente confermato">
                    <span className="chip ok">
                      <span className="dot"></span>
                      {fmtDateTime(data.cliente_confermato_at)}
                    </span>
                  </Kv>
                )}
                {data.cliente_disdetto_at && (
                  <Kv k="Cliente disdetto">
                    <span className="chip risk">
                      {fmtDateTime(data.cliente_disdetto_at)}
                    </span>
                  </Kv>
                )}
              </div>
            </div>
          </section>

          {/* GOOGLE CALENDAR SYNC */}
          <CalendarSyncSection
            appt={data}
            canEdit={canEdit}
            calendarStatus={calendarStatusQ.data}
            calendarStatusLoading={calendarStatusQ.isLoading}
            botStatus={calendarBotQ.data}
            syncMut={syncMut}
            unsyncMut={unsyncMut}
          />

          {/* CUSTODE — sempre presente (form modifica anche se vuoto) */}
          <section className="section" id="custode">
            <div className="section-head">
              <div className="title">Custode</div>
              {data.sollecito_count > 0 && (
                <span className="chip warn" style={{ marginLeft: 'auto' }}>
                  ⚠ {data.sollecito_count} solleciti
                </span>
              )}
              {canEdit && (
                <div className="actions">
                  <button
                    type="button"
                    className="btn sm ghost"
                    onClick={() => setCustodeOpen(true)}
                    title="Modifica nome e contatti custode"
                  >
                    ✎ Modifica
                  </button>
                </div>
              )}
            </div>
            <div className="section-body">
              <div className="kv-grid">
                <Kv k="Nome">{data.custode ?? '—'}</Kv>
                <Kv k="Telefono">
                  {data.custode_telefono ? (
                    <a
                      className="mono"
                      href={`tel:${data.custode_telefono}`}
                    >
                      {data.custode_telefono}
                    </a>
                  ) : (
                    '—'
                  )}
                </Kv>
                <Kv k="Email">
                  {data.custode_email ? (
                    <a
                      className="mono"
                      href={`mailto:${data.custode_email}`}
                    >
                      {data.custode_email}
                    </a>
                  ) : (
                    '—'
                  )}
                </Kv>
                {data.data_richiesta_custode && (
                  <Kv k="Richiesta a custode">
                    {fmtDateTime(data.data_richiesta_custode)}
                  </Kv>
                )}
              </div>
              <ProfessionalPicker
                tipo="custode"
                value={data.custode_ref ?? null}
                disabled={!canEdit}
                onChange={async (prof) => {
                  await patchAppointmentCustode(data.id, prof?.id ?? null)
                  void qc.invalidateQueries({ queryKey: ['appointment', id] })
                  void qc.invalidateQueries({ queryKey: ['appointments'] })
                }}
              />
            </div>
          </section>

          {/* NOTES + FEEDBACK */}
          <section className="section" id="note">
            <div className="section-head">
              <div className="title">Note</div>
              <div className="actions">
                {!editingNotes && canEdit && (
                  <button
                    type="button"
                    className="btn sm ghost"
                    onClick={() => startEditNotes(data)}
                  >
                    Modifica
                  </button>
                )}
                {editingNotes && (
                  <>
                    <button
                      type="button"
                      className="btn sm ghost"
                      onClick={() => setEditingNotes(false)}
                    >
                      Annulla
                    </button>
                    <button
                      type="button"
                      className="btn sm accent"
                      onClick={saveNotes}
                      disabled={patch.isPending}
                    >
                      {patch.isPending ? 'Salvo…' : 'Salva'}
                    </button>
                  </>
                )}
              </div>
            </div>
            <div
              className="section-body"
              style={{ padding: 'var(--sp-3) var(--sp-4)' }}
            >
              {!editingNotes && (
                <div
                  style={{
                    whiteSpace: 'pre-wrap',
                    fontSize: 'var(--fs-13)',
                    color: 'var(--ink-1)',
                    minHeight: 40,
                  }}
                >
                  {data.notes || (
                    <span className="muted" style={{ fontStyle: 'italic' }}>
                      Nessuna nota.
                    </span>
                  )}
                </div>
              )}
              {editingNotes && (
                <textarea
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  rows={5}
                  style={{
                    width: '100%',
                    padding: 8,
                    border: '1px solid var(--line)',
                    borderRadius: 'var(--r-sm)',
                    background: 'var(--surface)',
                    color: 'var(--ink-1)',
                    fontSize: 'var(--fs-13)',
                  }}
                />
              )}
              {data.feedback_post && (
                <div
                  style={{
                    marginTop: 'var(--sp-3)',
                    paddingTop: 'var(--sp-2)',
                    borderTop: '1px solid var(--line)',
                  }}
                >
                  <div
                    className="muted"
                    style={{
                      fontSize: 'var(--fs-10)',
                      textTransform: 'uppercase',
                      letterSpacing: 'var(--tracking-caps)',
                      marginBottom: 4,
                    }}
                  >
                    Feedback post-sopralluogo
                  </div>
                  <div
                    style={{
                      whiteSpace: 'pre-wrap',
                      fontSize: 'var(--fs-13)',
                    }}
                  >
                    {data.feedback_post}
                  </div>
                </div>
              )}
            </div>
          </section>

          <ActivityTimeline
            params={{ property_id: data.property_id ?? undefined, page_size: 30 }}
          />
        </div>
      )}
      {communicateOpen && data && (
        <CommunicateClientModal
          appt={data}
          onClose={() => setCommunicateOpen(false)}
          onSave={(payload) => {
            patch.mutate(payload, {
              onSuccess: () => setCommunicateOpen(false),
            })
          }}
        />
      )}
      {custodeOpen && data && (
        <CustodeEditModal
          appt={data}
          onClose={() => setCustodeOpen(false)}
          onSave={(payload) => {
            patch.mutate(payload, {
              onSuccess: () => setCustodeOpen(false),
            })
          }}
        />
      )}
      {sollCustodeOpen && (
        <SollecitoCustodeModal
          onClose={() => setSollCustodeOpen(false)}
          onSave={(body) => sollecitoMut.mutate(body)}
          pending={sollecitoMut.isPending}
        />
      )}
      {riprogrammaOpen && data && (
        <RiprogrammaModal
          appt={data}
          onClose={() => setRiprogrammaOpen(false)}
          onSave={(iso) => riprogrammaMut.mutate(iso)}
          pending={riprogrammaMut.isPending}
          error={riprogrammaMut.error?.message}
        />
      )}
      {chiudiOpen && data && (
        <ChiudiSopralluogoModal
          onClose={() => setChiudiOpen(false)}
          onConfirm={(status, reason, extra) => {
            setChiudiOpen(false)
            const patchPayload: Parameters<typeof patch.mutate>[0] = {
              status,
              ...(reason ? { outcome_reason: reason } : {}),
            }
            patch.mutate(patchPayload, {
              onSuccess: () => {
                if (extra?.vuole_scd) scdMut.mutate()
                if (status === 'effettuato') setInteresseModalOpen(true)
              },
            })
          }}
          pending={patch.isPending}
        />
      )}
      {interesseModalOpen && data && (
        <InteresseCollectorModal
          pending={patch.isPending}
          onSave={(interesse, motivo, vuoleScd) => {
            patch.mutate(
              {
                esito_interesse: interesse,
                ...(motivo ? { motivo_non_interesse: motivo } : {}),
              },
              {
                onSuccess: () => {
                  if (vuoleScd) scdMut.mutate()
                  setInteresseModalOpen(false)
                },
              },
            )
          }}
          onClose={() => setInteresseModalOpen(false)}
        />
      )}
      {data && (
        <NewChatModal
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          contextType="appointment"
          appointmentId={id}
          contextLabel={`${sopCode} · ${KIND_LABEL[data.kind] ?? data.kind}`}
        />
      )}
    </>
  )
}

function SideStatusBtn({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: string
  label: string
  onClick: () => void
  disabled: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 10px',
        fontSize: 'var(--fs-11)',
        background: disabled ? 'var(--bg-2)' : 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-sm)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        color: 'var(--ink-2)',
      }}
    >
      <span aria-hidden>{icon}</span>
      {label}
    </button>
  )
}

// Backend `is_syncable`: kind=sopralluogo + status in (attesa_riscontro_cliente,
// fissato) + scheduled_at!=null.
// Keep this in sync with backend/app/services/calendar_event_text.py:is_syncable.
function appointmentSyncability(appt: AppointmentRow): {
  ok: boolean
  reason: string | null
} {
  if (appt.kind !== 'sopralluogo') {
    return { ok: false, reason: 'Solo i sopralluoghi sono sincronizzabili.' }
  }
  if (appt.status !== 'fissato' && appt.status !== 'attesa_riscontro_cliente') {
    return {
      ok: false,
      reason: 'Disponibile da "Attesa conferma Cliente" in poi.',
    }
  }
  if (!appt.scheduled_at) {
    return { ok: false, reason: 'Manca data/ora schedulata.' }
  }
  return { ok: true, reason: null }
}

function CalendarSyncSection({
  appt,
  canEdit,
  calendarStatus,
  calendarStatusLoading,
  botStatus,
  syncMut,
  unsyncMut,
}: {
  appt: AppointmentRow
  canEdit: boolean
  calendarStatus: CalendarStatusResponse | undefined
  calendarStatusLoading: boolean
  botStatus: CalendarBotStatusResponse | undefined
  syncMut: {
    mutate: () => void
    isPending: boolean
    isError: boolean
    error: ApiError | null
  }
  unsyncMut: {
    mutate: () => void
    isPending: boolean
    isError: boolean
    error: ApiError | null
  }
}) {
  // Bot mode: if backend has configured a central bot identity AND the bot
  // has completed OAuth, sync works for everyone regardless of per-user
  // Google connection. Otherwise legacy mode (relies on click-user OAuth).
  const botActive = Boolean(botStatus?.enabled && botStatus?.connected)
  const userConnected = Boolean(calendarStatus?.connected)
  const connected = botActive || userConnected
  const synced = Boolean(appt.gcal_event_id)
  const syncability = appointmentSyncability(appt)
  const busy = syncMut.isPending || unsyncMut.isPending
  const errMsg = syncMut.error?.message ?? unsyncMut.error?.message

  return (
    <section className="section" id="calendar-sync">
      <div className="section-head">
        <div className="title">📅 Calendar Aste condiviso</div>
        {synced && (
          <span
            className="chip ok"
            style={{ marginLeft: 'auto' }}
            title={
              appt.gcal_last_synced_at
                ? `Ultimo sync: ${new Date(appt.gcal_last_synced_at).toLocaleString('it-IT')}`
                : undefined
            }
          >
            ✓ Sincronizzato
          </span>
        )}
      </div>
      <div
        className="section-body"
        style={{ padding: 'var(--sp-3) var(--sp-4)' }}
      >
        {calendarStatusLoading && (
          <div className="muted" style={{ fontSize: 'var(--fs-12)' }}>
            Verifica connessione…
          </div>
        )}

        {!calendarStatusLoading && !connected && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              flexWrap: 'wrap',
              fontSize: 'var(--fs-13)',
            }}
          >
            {botStatus?.enabled && !botStatus.connected ? (
              <span className="muted">
                Calendar bot ({botStatus.bot_email}) non ha completato OAuth.
                Contattare admin.
              </span>
            ) : (
              <>
                <span className="muted">
                  Google Calendar non connesso.
                </span>
                <Link to="/profile" className="link">
                  Collega dal profilo →
                </Link>
              </>
            )}
          </div>
        )}

        {!calendarStatusLoading && connected && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-2)',
            }}
          >
            {botActive && (
              <div
                className="muted"
                style={{ fontSize: 'var(--fs-11)' }}
              >
                Sync gestito da <b>{botStatus?.bot_email}</b> (visibile da
                tutto il team).
              </div>
            )}
            {synced && appt.gcal_last_synced_at && (
              <div
                className="muted"
                style={{ fontSize: 'var(--fs-12)' }}
              >
                Ultimo sync:{' '}
                <b style={{ color: 'var(--ink-1)' }}>
                  {new Date(appt.gcal_last_synced_at).toLocaleString('it-IT')}
                </b>
              </div>
            )}

            {!synced && !syncability.ok && (
              <div className="muted" style={{ fontSize: 'var(--fs-12)' }}>
                {syncability.reason}
              </div>
            )}

            {errMsg && (
              <div
                className="chip risk"
                role="alert"
                style={{ fontSize: 'var(--fs-12)' }}
              >
                Errore: {errMsg}
              </div>
            )}

            <div
              style={{
                display: 'flex',
                gap: 'var(--sp-2)',
                flexWrap: 'wrap',
              }}
            >
              {!synced && (
                <button
                  type="button"
                  className="btn sm accent"
                  onClick={() => syncMut.mutate()}
                  disabled={!canEdit || !syncability.ok || busy}
                  title={
                    !canEdit
                      ? 'Non hai permessi di scrittura'
                      : syncability.reason ?? undefined
                  }
                >
                  {syncMut.isPending
                    ? 'Sincronizzo…'
                    : '🗓 Sincronizza su Calendar Aste'}
                </button>
              )}
              {synced && (
                <>
                  <button
                    type="button"
                    className="btn sm"
                    onClick={() => syncMut.mutate()}
                    disabled={!canEdit || !syncability.ok || busy}
                    title="Aggiorna evento Google Calendar con i dati attuali"
                  >
                    {syncMut.isPending ? 'Aggiorno…' : '↻ Aggiorna evento'}
                  </button>
                  <button
                    type="button"
                    className="btn sm ghost"
                    onClick={() => {
                      if (
                        window.confirm(
                          'Rimuovere evento da Google Calendar?',
                        )
                      ) {
                        unsyncMut.mutate()
                      }
                    }}
                    disabled={!canEdit || busy}
                  >
                    {unsyncMut.isPending ? 'Rimuovo…' : '🗑 Rimuovi sync'}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

function CustodeEditModal({
  appt,
  onClose,
  onSave,
}: {
  appt: AppointmentRow
  onClose: () => void
  onSave: (payload: {
    custode: string | null
    custode_telefono: string | null
    custode_email: string | null
  }) => void
}) {
  const [nome, setNome] = useState(appt.custode ?? '')
  const [tel, setTel] = useState(appt.custode_telefono ?? '')
  const [email, setEmail] = useState(appt.custode_email ?? '')

  function handleSave() {
    onSave({
      custode: nome.trim() || null,
      custode_telefono: tel.trim() || null,
      custode_email: email.trim() || null,
    })
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 'var(--sp-3)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-4)',
          maxWidth: 460,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 'var(--fs-16)' }}>
            ✎ Modifica custode
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 0,
              fontSize: 18,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>
        <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-12)' }}>
          Dati custode usati per anteprima messaggio comunicazione cliente.
        </p>
        <div className="form-field">
          <label>Nome custode</label>
          <input
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Studio Galli & Ass."
          />
        </div>
        <div className="form-field">
          <label>Telefono</label>
          <input
            type="tel"
            value={tel}
            onChange={(e) => setTel(e.target.value)}
            placeholder="06 5577 8900"
            style={{ fontFamily: 'var(--font-mono)' }}
          />
        </div>
        <div className="form-field">
          <label>Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="custode@studio.it"
            style={{ fontFamily: 'var(--font-mono)' }}
          />
        </div>
        <div
          style={{
            display: 'flex',
            gap: 'var(--sp-2)',
            justifyContent: 'flex-end',
          }}
        >
          <button type="button" className="btn secondary" onClick={onClose}>
            Annulla
          </button>
          <button type="button" className="btn accent" onClick={handleSave}>
            Salva
          </button>
        </div>
      </div>
    </div>
  )
}

function RiprogrammaModal({
  appt,
  onClose,
  onSave,
  pending,
  error,
}: {
  appt: AppointmentRow
  onClose: () => void
  onSave: (riattivaIso: string) => void
  pending: boolean
  error?: string
}) {
  // riattiva_il dev'essere futura e > data del sopralluogo annullato.
  const minDate = (() => {
    const base = appt.scheduled_at ? new Date(appt.scheduled_at) : new Date()
    const d = base > new Date() ? base : new Date()
    d.setDate(d.getDate() + 1)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })()
  const [data, setData] = useState(minDate)
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 'var(--sp-3)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-4)',
          maxWidth: 440,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--fs-16)' }}>🔁 Riprogramma</h2>
        <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-12)' }}>
          Chiude questa richiesta e ne crea una nuova "Richiesta da fare", che
          riapparirà in cima solo dalla data scelta (snooze).
        </p>
        <div className="form-field">
          <label>Riattiva dal *</label>
          <input
            type="date"
            value={data}
            min={minDate}
            onChange={(e) => setData(e.target.value)}
          />
        </div>
        {error && (
          <span className="chip risk" style={{ fontSize: 'var(--fs-11)' }}>
            {error}
          </span>
        )}
        <div
          style={{
            display: 'flex',
            gap: 'var(--sp-2)',
            justifyContent: 'flex-end',
          }}
        >
          <button type="button" className="btn sm" onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className="btn sm accent"
            disabled={pending || !data}
            onClick={() =>
              onSave(new Date(data + 'T09:00:00').toISOString())
            }
          >
            Riprogramma
          </button>
        </div>
      </div>
    </div>
  )
}

// Descrizioni brevi per ogni motivo di chiusura negativo (chiarezza #17).
const CLOSE_DESC: Record<string, string> = {
  disdetto_cliente: 'Il cliente ha disdetto',
  annullato_custode: 'Il custode ha annullato',
  mancata_conferma: 'Il cliente non ha confermato in tempo',
  noshow_cliente: 'Il cliente non si è presentato',
  annullata_consulente: 'Richiesta annullata dal consulente',
}

// Chiusura unificata (#17): un solo modale con esito positivo / negativo+motivo
// / sospeso. Sostituisce i bottoni sparsi.
function ChiudiSopralluogoModal({
  onClose,
  onConfirm,
  pending,
}: {
  onClose: () => void
  onConfirm: (
    status: AppointmentStatus,
    reason?: OutcomeReason,
    extra?: { vuole_scd?: boolean }
  ) => void
  pending: boolean
}) {
  const [sel, setSel] = useState<string | null>(null)
  const [vuoleScdNeg, setVuoleScdNeg] = useState(false)
  type Choice = {
    key: string
    status: AppointmentStatus
    reason?: OutcomeReason
    icon: string
    label: string
    desc: string
  }
  const positive: Choice = {
    key: 'effettuato',
    status: 'effettuato',
    icon: '✅',
    label: 'Effettuato',
    desc: 'Sopralluogo svolto regolarmente',
  }
  const negatives: Choice[] = CLOSE_OPTIONS.map((o) => ({
    key: o.reason,
    status: o.status as AppointmentStatus,
    reason: o.reason,
    icon: o.icon,
    label: o.label,
    desc: CLOSE_DESC[o.reason] ?? '',
  }))
  const sospeso: Choice = {
    key: 'sospeso',
    status: 'sospeso',
    icon: '⏸',
    label: 'Sospeso',
    desc: 'Metti in pausa, riprendibile',
  }
  const all = [positive, ...negatives, sospeso]
  const chosen = all.find((o) => o.key === sel) ?? null

  const renderRow = (o: Choice) => {
    const active = sel === o.key
    return (
      <button
        key={o.key}
        type="button"
        onClick={() => setSel(o.key)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          padding: 'var(--sp-2) var(--sp-3)',
          textAlign: 'left',
          border: active
            ? '2px solid var(--accent)'
            : '1px solid var(--line)',
          background: active ? 'var(--bg-2)' : 'transparent',
          borderRadius: 'var(--r-sm)',
          cursor: 'pointer',
          width: '100%',
        }}
      >
        <span style={{ fontSize: 'var(--fs-16)' }}>{o.icon}</span>
        <span style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 'var(--fw-semibold)' }}>{o.label}</span>
          {o.desc && (
            <span className="muted" style={{ fontSize: 'var(--fs-11)' }}>
              {o.desc}
            </span>
          )}
        </span>
      </button>
    )
  }

  const GroupTitle = ({ children }: { children: string }) => (
    <span
      className="muted"
      style={{
        fontSize: 'var(--fs-10)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--tracking-caps)',
        fontWeight: 'var(--fw-semibold)',
      }}
    >
      {children}
    </span>
  )

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 'var(--sp-3)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-4)',
          maxWidth: 460,
          width: '100%',
          maxHeight: '85vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--fs-16)' }}>
          🔒 Chiudi sopralluogo
        </h2>
        <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-12)' }}>
          Scegli l'esito. La chiusura è reversibile con "Riapri".
        </p>

        <GroupTitle>Esito positivo</GroupTitle>
        {renderRow(positive)}

        <GroupTitle>Esito negativo — con motivo</GroupTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          {negatives.map(renderRow)}
        </div>

        <GroupTitle>Altro</GroupTitle>
        {renderRow(sospeso)}

        {chosen && chosen.key !== 'effettuato' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', fontSize: 'var(--fs-12)', cursor: 'pointer', padding: 'var(--sp-2)', background: 'var(--bg-2)', borderRadius: 'var(--r-sm)', border: '1px solid var(--line)' }}>
            <input type="checkbox" checked={vuoleScdNeg} onChange={e => setVuoleScdNeg(e.target.checked)} />
            📋 Richiedi comunque SCD fattibilità
          </label>
        )}

        <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end', marginTop: 'var(--sp-2)' }}>
          <button type="button" className="btn sm" onClick={onClose}>Annulla</button>
          <button
            type="button"
            className="btn sm accent"
            disabled={!chosen || pending}
            onClick={() => {
              if (!chosen) return
              onConfirm(chosen.status, chosen.reason, { vuole_scd: vuoleScdNeg })
            }}
          >
            {pending ? '…' : 'Conferma chiusura'}
          </button>
        </div>
      </div>
    </div>
  )
}

function InteresseCollectorModal({
  pending,
  onSave,
  onClose,
}: {
  pending: boolean
  onSave: (interesse: boolean, motivo: string, vuoleScd: boolean) => void
  onClose: () => void
}) {
  const [interesse, setInteresse] = useState<'si' | 'no' | null>(null)
  const [vuoleScd, setVuoleScd] = useState(false)
  const [motivoNo, setMotivoNo] = useState('')

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 'var(--sp-3)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-4)',
          maxWidth: 460,
          width: '100%',
          maxHeight: '85vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--fs-16)' }}>
          ✅ Sopralluogo effettuato — interesse cliente
        </h2>
        <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-12)' }}>
          Registra se il cliente è interessato a procedere.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          {([
            { key: 'si', icon: '✅', label: 'Sì, interessato', desc: 'Vuole procedere con SCD e/o partecipazione' },
            { key: 'no', icon: '❌', label: 'No, non interessa', desc: 'Non vuole procedere' },
          ] as const).map(o => (
            <button
              key={o.key}
              type="button"
              onClick={() => setInteresse(o.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
                padding: 'var(--sp-2) var(--sp-3)', textAlign: 'left',
                border: interesse === o.key ? '2px solid var(--accent)' : '1px solid var(--line)',
                background: interesse === o.key ? 'var(--bg-2)' : 'transparent',
                borderRadius: 'var(--r-sm)', cursor: 'pointer', width: '100%',
              }}
            >
              <span style={{ fontSize: 'var(--fs-16)' }}>{o.icon}</span>
              <span style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontWeight: 'var(--fw-semibold)' }}>{o.label}</span>
                <span className="muted" style={{ fontSize: 'var(--fs-11)' }}>{o.desc}</span>
              </span>
            </button>
          ))}
        </div>

        {interesse === 'si' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', padding: 'var(--sp-2)', background: 'var(--bg-2)', borderRadius: 'var(--r-sm)' }}>
            <span style={{ fontSize: 'var(--fs-12)', fontWeight: 'var(--fw-semibold)' }}>Azione da avviare:</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', fontSize: 'var(--fs-13)', cursor: 'pointer' }}>
              <input type="checkbox" checked={vuoleScd} onChange={e => setVuoleScd(e.target.checked)} />
              📋 Crea scheda di fattibilità (SCD)
            </label>
            <span className="muted" style={{ fontSize: 'var(--fs-11)' }}>
              Partecipazione: aprila dalla scheda lotto dopo la chiusura.
            </span>
          </div>
        )}

        {interesse === 'no' && (
          <div>
            <label style={{ fontSize: 'var(--fs-12)', display: 'block', marginBottom: 4 }}>Perché non interessa?</label>
            <textarea
              rows={3}
              value={motivoNo}
              onChange={e => setMotivoNo(e.target.value)}
              placeholder="Es. prezzo troppo alto, zona non giusta, immobile in cattive condizioni…"
              style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: 'var(--fs-13)', padding: 'var(--sp-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-sm)' }}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end', marginTop: 'var(--sp-2)' }}>
          <button type="button" className="btn sm" onClick={onClose}>Più tardi</button>
          <button
            type="button"
            className="btn sm accent"
            disabled={pending || interesse === null || (interesse === 'no' && !motivoNo.trim())}
            onClick={() => {
              if (interesse === null) return
              onSave(interesse === 'si', interesse === 'no' ? motivoNo.trim() : '', interesse === 'si' && vuoleScd)
            }}
          >
            {pending ? '…' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  )
}

function InteresseInlineEditor({
  current,
  currentMotivo,
  pending,
  onSave,
  onCancel,
}: {
  current: boolean | null
  currentMotivo: string
  pending: boolean
  onSave: (interesse: boolean, motivo: string) => void
  onCancel: () => void
}) {
  const [val, setVal] = useState<'si' | 'no' | null>(
    current === true ? 'si' : current === false ? 'no' : null,
  )
  const [motivo, setMotivo] = useState(currentMotivo)

  return (
    <div style={{
      marginTop: 'var(--sp-2)',
      padding: 'var(--sp-3)',
      background: 'var(--bg-2)',
      borderRadius: 'var(--r-sm)',
      border: '1px solid var(--line)',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--sp-2)',
    }}>
      <span style={{ fontSize: 'var(--fs-12)', fontWeight: 'var(--fw-semibold)' }}>Interesse cliente</span>
      <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
        {(['si', 'no'] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setVal(k)}
            style={{
              flex: 1, padding: 'var(--sp-2)', cursor: 'pointer',
              border: val === k ? '2px solid var(--accent)' : '1px solid var(--line)',
              background: val === k ? 'var(--bg-3)' : 'transparent',
              borderRadius: 'var(--r-sm)', fontSize: 'var(--fs-13)',
            }}
          >
            {k === 'si' ? '✅ Sì, interessato' : '❌ No, non interessa'}
          </button>
        ))}
      </div>
      {val === 'no' && (
        <textarea
          rows={2}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Perché non interessa?"
          style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: 'var(--fs-13)', padding: 'var(--sp-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-sm)' }}
        />
      )}
      <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
        <button type="button" className="btn sm ghost" onClick={onCancel}>Annulla</button>
        <button
          type="button"
          className="btn sm accent"
          disabled={pending || val === null || (val === 'no' && !motivo.trim())}
          onClick={() => { if (val) onSave(val === 'si', val === 'no' ? motivo.trim() : '') }}
        >
          {pending ? '…' : 'Salva'}
        </button>
      </div>
    </div>
  )
}

function SollecitoCustodeModal({
  onClose,
  onSave,
  pending,
}: {
  onClose: () => void
  onSave: (body: SollecitoBody) => void
  pending: boolean
}) {
  const [esito, setEsito] =
    useState<SollecitoBody['esito_custode']>('sopralluoghi_a_breve')
  const [nota, setNota] = useState('')
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 'var(--sp-3)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-4)',
          maxWidth: 440,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--fs-16)' }}>
          📞 Registra chiamata custode
        </h2>
        <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-12)' }}>
          Data/ora automatiche. Scegli l'esito: si scrive da sola la nota.
        </p>
        <div className="form-field">
          <label>Esito</label>
          <select
            value={esito ?? ''}
            onChange={(e) =>
              setEsito(
                (e.target.value || null) as SollecitoBody['esito_custode'],
              )
            }
          >
            <option value="sopralluoghi_a_breve">Sopralluoghi a breve</option>
            <option value="troppe_visite">
              Troppe visite in organizzazione
            </option>
            <option value="visite_terminate">Visite terminate</option>
            <option value="">Nota libera…</option>
          </select>
        </div>
        {!esito && (
          <div className="form-field">
            <label>Nota</label>
            <textarea
              rows={2}
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
        )}
        <div
          style={{
            display: 'flex',
            gap: 'var(--sp-2)',
            justifyContent: 'flex-end',
          }}
        >
          <button type="button" className="btn sm" onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className="btn sm accent"
            disabled={pending || (!esito && !nota.trim())}
            onClick={() =>
              onSave({
                tipo: 'custode',
                esito_custode: esito || null,
                nota: esito ? null : nota.trim() || null,
              })
            }
          >
            Salva
          </button>
        </div>
      </div>
    </div>
  )
}

function CommunicateClientModal({
  appt,
  onClose,
  onSave,
}: {
  appt: AppointmentRow
  onClose: () => void
  onSave: (payload: {
    status: AppointmentStatus
    scheduled_at: string
    notes: string
    modalita: 'fisico' | 'remoto'
    scadenza_conferma_cliente: string
  }) => void
}) {
  // Prefill data/ora corrente + 2 giorni se mai schedulato.
  const initialDt = appt.scheduled_at
    ? new Date(appt.scheduled_at)
    : (() => {
        const d = new Date()
        d.setDate(d.getDate() + 2)
        d.setHours(10, 0, 0, 0)
        return d
      })()
  const initialIso = `${initialDt.getFullYear()}-${String(initialDt.getMonth() + 1).padStart(2, '0')}-${String(initialDt.getDate()).padStart(2, '0')}T${String(initialDt.getHours()).padStart(2, '0')}:${String(initialDt.getMinutes()).padStart(2, '0')}`

  const [scheduledAt, setScheduledAt] = useState(initialIso)
  const [custodeNotes, setCustodeNotes] = useState('')
  const [sentEmail, setSentEmail] = useState(false)
  const [sentWa, setSentWa] = useState(false)
  // Fase 2: modalità (fisico/remoto) + scadenza conferma cliente — obbligatori.
  const [modalita, setModalita] = useState<'' | 'fisico' | 'remoto'>(
    (appt.modalita as 'fisico' | 'remoto' | null) ?? '',
  )
  const initialScad = (() => {
    const d = new Date()
    d.setDate(d.getDate() + 2)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })()
  const [scadenzaConferma, setScadenzaConferma] = useState(initialScad)

  // Build email + WhatsApp message bodies
  const propRef = appt.property_address ?? appt.property_rge ?? 'immobile'
  const dt = new Date(scheduledAt)
  const dtFmt = dt.toLocaleString('it-IT', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  })
  const messageBody = `Gentile ${appt.client_name ?? 'cliente'},\n\nil custode ha confermato il sopralluogo per ${propRef} per ${dtFmt}.\n\nLe chiediamo conferma di partecipazione entro 24h.\n${custodeNotes ? '\nNote del custode: ' + custodeNotes + '\n' : ''}\nCordiali saluti,\nAGL Roma EUR`
  const emailSubject = `Sopralluogo ${propRef} — ${dtFmt}`
  const mailto = appt.client_email
    ? `mailto:${appt.client_email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(messageBody)}`
    : null
  const waHref = appt.client_phone
    ? `https://wa.me/${appt.client_phone.replace(/[^\d]/g, '')}?text=${encodeURIComponent(messageBody)}`
    : null

  function handleSave() {
    if (!modalita || !scadenzaConferma) return // obbligatori
    const newNotes = [
      appt.notes,
      `Comunicato cliente ${new Date().toLocaleDateString('it-IT')} via ${sentEmail ? 'email' : ''}${sentEmail && sentWa ? '+' : ''}${sentWa ? 'WhatsApp' : ''}${custodeNotes ? ' · Note custode: ' + custodeNotes : ''}`,
    ]
      .filter(Boolean)
      .join('\n')
    onSave({
      status: 'attesa_riscontro_cliente',
      scheduled_at: new Date(scheduledAt).toISOString(),
      notes: newNotes,
      modalita,
      // fine giornata della data scelta = deadline conferma cliente
      scadenza_conferma_cliente: new Date(
        scadenzaConferma + 'T23:59:00',
      ).toISOString(),
    })
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 'var(--sp-3)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-4)',
          maxWidth: 540,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 'var(--fs-16)' }}>
            📧 Comunica al cliente
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: 0, fontSize: 18, cursor: 'pointer' }}
          >
            ×
          </button>
        </div>
        <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-12)' }}>
          Custode ha confermato data/orario? Comunicalo al cliente via email +
          WhatsApp. Stato passerà a "Attesa riscontro cliente".
        </p>

        <div className="form-field">
          <label>Data e ora sopralluogo *</label>
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
          />
        </div>

        <div className="form-field">
          <label>Modalità *</label>
          <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
            <label
              style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
            >
              <input
                type="radio"
                name="modalita"
                checked={modalita === 'fisico'}
                onChange={() => setModalita('fisico')}
              />
              Fisico
            </label>
            <label
              style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
            >
              <input
                type="radio"
                name="modalita"
                checked={modalita === 'remoto'}
                onChange={() => setModalita('remoto')}
              />
              Remoto
            </label>
          </div>
        </div>

        <div className="form-field">
          <label>Scadenza conferma cliente *</label>
          <input
            type="date"
            value={scadenzaConferma}
            onChange={(e) => setScadenzaConferma(e.target.value)}
          />
          <span className="muted" style={{ fontSize: 'var(--fs-11)' }}>
            Se il cliente non conferma entro questa data, va chiuso come
            "Mancata conferma".
          </span>
        </div>

        <div className="form-field">
          <label>Note dal custode (opzionale)</label>
          <textarea
            rows={2}
            value={custodeNotes}
            onChange={(e) => setCustodeNotes(e.target.value)}
            placeholder="Es: ingresso lato cortile, citofono Galli…"
            style={{ width: '100%' }}
          />
        </div>

        <div
          style={{
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-sm)',
            padding: 'var(--sp-2) var(--sp-3)',
            background: 'var(--bg-2)',
            fontSize: 'var(--fs-11)',
            whiteSpace: 'pre-wrap',
            fontFamily: 'var(--font-mono)',
            color: 'var(--ink-2)',
            maxHeight: 140,
            overflowY: 'auto',
          }}
        >
          {messageBody}
        </div>

        <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
          {mailto ? (
            <a
              href={mailto}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setSentEmail(true)}
              className="btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              📧 Apri Email{sentEmail && ' ✓'}
            </a>
          ) : (
            <span className="muted" style={{ fontSize: 'var(--fs-11)' }}>
              Email cliente mancante
            </span>
          )}
          {waHref ? (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setSentWa(true)}
              className="btn"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                background: '#25d366',
                color: '#fff',
                borderColor: '#25d366',
              }}
            >
              💬 Apri WhatsApp{sentWa && ' ✓'}
            </a>
          ) : (
            <span className="muted" style={{ fontSize: 'var(--fs-11)' }}>
              Telefono cliente mancante
            </span>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            gap: 'var(--sp-2)',
            justifyContent: 'flex-end',
            marginTop: 'var(--sp-2)',
          }}
        >
          <button type="button" className="btn secondary" onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className="btn accent"
            onClick={handleSave}
            disabled={!scheduledAt || !modalita || !scadenzaConferma}
            title={
              !modalita
                ? 'Seleziona la modalità (fisico/remoto)'
                : !scadenzaConferma
                  ? 'Imposta la scadenza conferma cliente'
                  : !sentEmail && !sentWa
                    ? 'Suggerito: apri email/WhatsApp prima di salvare'
                    : ''
            }
          >
            Salva → Attesa cliente
          </button>
        </div>
      </div>
    </div>
  )
}
